/**
 * Country detection service.
 * Detects the user's country code via IP geolocation on the client side.
 * Result is cached in sessionStorage to avoid repeated API calls.
 */
import { normalizeCountryCode } from '../utils/pricingCountry';

const CACHE_KEY = 'detected_country';

let fetchPromise: Promise<string | null> | null = null;

function getCountryFromTimezone(): string | null {
  const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;

  if (timezone === 'Asia/Kolkata' || timezone === 'Asia/Calcutta') {
    return 'IN';
  }

  return null;
}

const isLocalHostname = (): boolean => (
  ['localhost', '127.0.0.1'].includes(window.location.hostname)
);

async function fetchCountryCode(): Promise<string | null> {
  const resp = await fetch('https://ipapi.co/country/');

  if (!resp.ok) return getCountryFromTimezone();

  return normalizeCountryCode(await resp.text());
}

async function fetchCountryFromIP(): Promise<string | null> {
  try {
    if (typeof window === 'undefined') return null;

    // Skip on localhost / prerender to avoid CORS errors
    if (isLocalHostname()) return getCountryFromTimezone();

    return fetchCountryCode();
  } catch {
    return getCountryFromTimezone();
  }
}

export function getCachedDetectedCountry(): string | null {
  if (typeof window === 'undefined') return null;

  try {
    return normalizeCountryCode(window.sessionStorage.getItem(CACHE_KEY));
  } catch {
    return null;
  }
}

/**
 * Get the user's detected country code (ISO 3166-1 alpha-2).
 * Uses a cached value if available, otherwise fetches from IP geolocation.
 * Safe to call multiple times — concurrent calls share the same fetch.
 */
export async function getDetectedCountry(): Promise<string | null> {
  const cached = getCachedDetectedCountry();

  if (cached) return cached;

  if (!fetchPromise) {
    fetchPromise = fetchCountryFromIP().then((country) => {
      if (country) {
        try {
          window.sessionStorage.setItem(CACHE_KEY, country);
        } catch {
          // Country detection should still succeed if storage is unavailable.
        }
      }

      fetchPromise = null;

      return country;
    });
  }

  return fetchPromise;
}
